import { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useRouterState } from '@tanstack/react-router';
import type { Control } from 'react-hook-form';
import { Button } from '@shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/ui/dialog';
import { Input } from '@shared/ui/input';
import { Label } from '@shared/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shared/ui/card';
import { toast } from '@shared/ui/toast';
import { cn } from '@shared/lib/cn';
import { type AuthRepository } from '../application/auth.repository';
import {
  credentialsSchema,
  signUpSchema,
  type Credentials,
  type SignUpInput,
} from '../domain/credentials';
import { useAuthRepository } from '../application/auth-context';

type Mode = 'sign-in' | 'sign-up';

/**
 * Map raw Supabase auth errors to safe, generic strings. Two reasons:
 * 1. Distinguishing "user not found" from "wrong password" leaks account
 *    presence. The neutral "неверный email или пароль" closes that channel.
 * 2. The default Supabase strings are English and inconsistent ("Invalid
 *    login credentials" vs "User not found" vs "Email not confirmed").
 */
function friendlySignInError(raw: string | null, t: (k: string) => string): string {
  if (!raw) return t('auth.signIn.errorGeneric');
  const lower = raw.toLowerCase();
  if (lower.includes('email') && lower.includes('confirm')) {
    return t('auth.signIn.errorEmailNotConfirmed');
  }
  // Anything else collapses to the generic message.
  return t('auth.signIn.errorGeneric');
}

const GOOGLE_REDIRECT_TIMEOUT_MS = 15_000;

interface AuthPageProps {
  pendingInviteCode?: string | null;
}

export function AuthPage({ pendingInviteCode = null }: AuthPageProps = {}): React.JSX.Element {
  const { t } = useTranslation();
  // URL-driven mode: ?mode=signup → sign-up form, otherwise sign-in. A pending
  // invite code still forces sign-up regardless (the invite flow lands the
  // user expecting to create an account). This is router-only — the auth
  // page is rendered above whatever route the AuthGuard intercepted, so we
  // sync state from window.location.search without owning a route definition.
  // Tanstack router parses location.search into an object keyed by name. We
  // only need a single string param, and AuthPage doesn't own a route to
  // declare typed search on, so reach into the parsed bag directly.
  const urlMode = useRouterState({
    select: (s) => {
      const search = s.location.search as Record<string, unknown> | undefined;
      const raw = search?.mode;
      return typeof raw === 'string' ? raw : null;
    },
  });
  const initialMode: Mode = pendingInviteCode || urlMode === 'signup' ? 'sign-up' : 'sign-in';
  const [mode, setMode] = useState<Mode>(initialMode);
  // We deliberately don't sync state on subsequent urlMode changes — the
  // sticky form mode lives in component state once mounted. switchMode() is
  // the only path that touches both at once. A user landing via browser
  // back/forward will re-mount AuthPage and pick up the URL via init.
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [signedUpEmail, setSignedUpEmail] = useState<string | null>(null);
  const repo = useAuthRepository();
  const googleTimeoutRef = useRef<number | null>(null);

  // Tear down the Google-redirect watchdog on unmount.
  useEffect(() => {
    return () => {
      if (googleTimeoutRef.current !== null) {
        window.clearTimeout(googleTimeoutRef.current);
      }
    };
  }, []);

  const isSignUp = mode === 'sign-up';

  const signUpForm = useForm<SignUpInput>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { email: '', password: '', displayName: '' },
  });
  const signInForm = useForm<Credentials>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { email: '', password: '' },
  });

  async function onSubmitSignUp(values: SignUpInput): Promise<void> {
    setError(null);
    setInfo(null);
    try {
      await repo.signUp(values);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Не удалось зарегистрироваться';
      // signUp throws a friendly message when email confirmation is on — surface as info, not error.
      if (msg.includes('Подтверди')) {
        setInfo(msg);
        setSignedUpEmail(values.email);
      } else {
        setError(msg);
      }
    }
  }

  async function onSubmitSignIn(values: Credentials): Promise<void> {
    setError(null);
    setInfo(null);
    try {
      await repo.signIn(values);
    } catch (e) {
      const raw = e instanceof Error ? e.message : null;
      setError(friendlySignInError(raw, t));
    }
  }

  async function onGoogle(): Promise<void> {
    setError(null);
    setInfo(null);
    setGoogleLoading(true);
    // Some Google redirect flows (popup blocked, user dismisses chooser
    // mid-handshake) leave us hung in "redirecting" forever. Hard-cap the
    // spinner at 15s and surface a recovery hint instead.
    googleTimeoutRef.current = window.setTimeout(() => {
      setGoogleLoading(false);
      setError(t('auth.google.timeout'));
    }, GOOGLE_REDIRECT_TIMEOUT_MS);
    try {
      await repo.signInWithGoogle();
    } catch (e) {
      if (googleTimeoutRef.current !== null) {
        window.clearTimeout(googleTimeoutRef.current);
        googleTimeoutRef.current = null;
      }
      setGoogleLoading(false);
      setError(e instanceof Error ? e.message : t('auth.google.error'));
    }
  }

  async function resendConfirmation(): Promise<void> {
    if (!signedUpEmail) return;
    try {
      // Re-trigger Supabase's signup email by re-signing-up with the same
      // address; the API treats the second call as a resend when the user
      // is still pending confirmation.
      await repo.requestPasswordReset(signedUpEmail);
      toast.success(t('auth.signUp.resentToast'));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('auth.signUp.resendError'));
    }
  }

  function switchMode(next: Mode): void {
    setMode(next);
    setError(null);
    setInfo(null);
    // Sync URL so refresh / share keeps the user on the form they chose.
    // pushState (not router.navigate) — AuthPage isn't a route, it shadows
    // any route, so we just rewrite the search string without unmounting.
    const params = new URLSearchParams(window.location.search);
    if (next === 'sign-up') params.set('mode', 'signup');
    else params.delete('mode');
    const query = params.toString();
    const url = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', url);
  }

  return (
    <main className="flex min-h-full items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{isSignUp ? t('auth.signUp.title') : t('auth.signIn.title')}</CardTitle>
          <CardDescription>
            {isSignUp ? t('auth.signUp.subtitle') : t('auth.signIn.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {pendingInviteCode ? (
            <p
              className="border-brand/30 bg-brand/10 text-foreground rounded-md border p-3 text-sm"
              role="status"
            >
              {t('relationships.invites.pendingBanner', { code: pendingInviteCode })}
            </p>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="w-full"
            onClick={() => {
              void onGoogle();
            }}
            disabled={googleLoading}
          >
            <GoogleIcon className="size-5" aria-hidden="true" />
            {googleLoading ? t('auth.google.redirecting') : t('auth.google.continue')}
          </Button>

          <div className="flex items-center gap-3">
            <div className="bg-border h-px flex-1" />
            <span className="text-muted-foreground text-xs uppercase">{t('auth.or')}</span>
            <div className="bg-border h-px flex-1" />
          </div>

          {isSignUp ? (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                void signUpForm.handleSubmit(onSubmitSignUp)(e);
              }}
              noValidate
              aria-busy={signUpForm.formState.isSubmitting || undefined}
            >
              <div className="space-y-2">
                <Label htmlFor="displayName">{t('auth.fields.displayName')}</Label>
                <Input
                  id="displayName"
                  autoComplete="name"
                  maxLength={60}
                  {...signUpForm.register('displayName')}
                  aria-invalid={Boolean(signUpForm.formState.errors.displayName) || undefined}
                  aria-describedby={
                    signUpForm.formState.errors.displayName ? 'displayName-error' : undefined
                  }
                />
                {signUpForm.formState.errors.displayName ? (
                  <p id="displayName-error" className="text-destructive text-sm">
                    {signUpForm.formState.errors.displayName.message}
                  </p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="email-up">Email</Label>
                <Input
                  id="email-up"
                  type="email"
                  autoComplete="email"
                  {...signUpForm.register('email')}
                  aria-invalid={Boolean(signUpForm.formState.errors.email) || undefined}
                  aria-describedby={
                    signUpForm.formState.errors.email ? 'email-up-error' : undefined
                  }
                />
                {signUpForm.formState.errors.email ? (
                  <p id="email-up-error" className="text-destructive text-sm">
                    {signUpForm.formState.errors.email.message}
                  </p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="password-up">{t('auth.fields.password')}</Label>
                <Input
                  id="password-up"
                  type="password"
                  autoComplete="new-password"
                  {...signUpForm.register('password')}
                  aria-invalid={Boolean(signUpForm.formState.errors.password) || undefined}
                  aria-describedby={
                    signUpForm.formState.errors.password
                      ? 'password-up-error'
                      : 'password-up-strength'
                  }
                />
                <PasswordStrengthMeter id="password-up-strength" control={signUpForm.control} />
                {signUpForm.formState.errors.password ? (
                  <p id="password-up-error" className="text-destructive text-sm">
                    {signUpForm.formState.errors.password.message}
                  </p>
                ) : null}
              </div>
              <Button
                type="submit"
                size="lg"
                className="w-full"
                loading={signUpForm.formState.isSubmitting}
              >
                {t('auth.signUp.submit')}
              </Button>
            </form>
          ) : (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                void signInForm.handleSubmit(onSubmitSignIn)(e);
              }}
              noValidate
              aria-busy={signInForm.formState.isSubmitting || undefined}
            >
              <div className="space-y-2">
                <Label htmlFor="email-in">Email</Label>
                <Input
                  id="email-in"
                  type="email"
                  autoComplete="email"
                  {...signInForm.register('email')}
                  aria-invalid={Boolean(signInForm.formState.errors.email) || undefined}
                  aria-describedby={
                    signInForm.formState.errors.email ? 'email-in-error' : undefined
                  }
                />
                {signInForm.formState.errors.email ? (
                  <p id="email-in-error" className="text-destructive text-sm">
                    {signInForm.formState.errors.email.message}
                  </p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="password-in">{t('auth.fields.password')}</Label>
                <Input
                  id="password-in"
                  type="password"
                  autoComplete="current-password"
                  {...signInForm.register('password')}
                  aria-invalid={Boolean(signInForm.formState.errors.password) || undefined}
                  aria-describedby={
                    signInForm.formState.errors.password ? 'password-in-error' : undefined
                  }
                />
                {signInForm.formState.errors.password ? (
                  <p id="password-in-error" className="text-destructive text-sm">
                    {signInForm.formState.errors.password.message}
                  </p>
                ) : null}
              </div>
              <Button
                type="submit"
                size="lg"
                className="w-full"
                loading={signInForm.formState.isSubmitting}
              >
                {t('auth.signIn.submit')}
              </Button>
              <ForgotPasswordTrigger initialEmail={signInForm.getValues('email')} repo={repo} />
            </form>
          )}

          {error ? (
            <p
              className="border-destructive/30 bg-destructive/10 text-destructive rounded-md border p-3 text-sm"
              role="alert"
            >
              {error}
            </p>
          ) : null}
          {info ? (
            <div
              className="border-border bg-muted text-muted-foreground space-y-2 rounded-md border p-3 text-sm"
              role="status"
            >
              <p>{info}</p>
              {signedUpEmail ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    void resendConfirmation();
                  }}
                >
                  {t('auth.signUp.resend')}
                </Button>
              ) : null}
            </div>
          ) : null}

          <div className="text-muted-foreground text-center text-sm">
            {isSignUp ? t('auth.toggle.haveAccount') : t('auth.toggle.noAccount')}{' '}
            <button
              type="button"
              onClick={() => {
                switchMode(isSignUp ? 'sign-in' : 'sign-up');
              }}
              className="text-foreground font-medium underline-offset-4 hover:underline"
            >
              {isSignUp ? t('auth.signIn.title') : t('auth.signUp.title')}
            </button>
          </div>
        </CardContent>
      </Card>
    </main>
  );
}

function ForgotPasswordTrigger({
  initialEmail,
  repo,
}: {
  initialEmail: string;
  repo: AuthRepository;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(initialEmail);
  const [submitting, setSubmitting] = useState(false);

  async function send(): Promise<void> {
    const trimmed = email.trim();
    if (trimmed === '') {
      toast.error(t('auth.forgot.emailRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await repo.requestPasswordReset(trimmed);
      toast.success(t('auth.forgot.sent'));
      setOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('auth.forgot.error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setEmail(initialEmail);
          setOpen(true);
        }}
        className="text-muted-foreground hover:text-foreground block w-full text-center text-sm underline-offset-4 hover:underline"
      >
        {t('auth.forgot.trigger')}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('auth.forgot.title')}</DialogTitle>
            <DialogDescription>{t('auth.forgot.hint')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="forgot-email">Email</Label>
            <Input
              id="forgot-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setOpen(false);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => {
                void send();
              }}
              loading={submitting}
            >
              {t('auth.forgot.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Rule-based 0-4 password strength meter. No zxcvbn — bundle cost is too high
 * for a hint that resolves on submit anyway. Each rule contributes 1 point:
 * length ≥ 8, mixed case, contains a digit, contains a symbol. Renders four
 * segmented bars + a label.
 */
function scorePassword(value: string): number {
  if (value.length === 0) return 0;
  let score = 0;
  if (value.length >= 8) score++;
  if (/[a-zа-я]/.test(value) && /[A-ZА-Я]/.test(value)) score++;
  if (/\d/.test(value)) score++;
  if (/[^A-Za-zА-Яа-я0-9]/.test(value)) score++;
  return score;
}

function PasswordStrengthMeter({
  id,
  control,
}: {
  id: string;
  control: Control<SignUpInput>;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const password = useWatch({ control, name: 'password' }) ?? '';
  if (password.length === 0) return null;
  const score = scorePassword(password);
  const labels = [
    t('auth.password.strength.tooShort'),
    t('auth.password.strength.weak'),
    t('auth.password.strength.fair'),
    t('auth.password.strength.good'),
    t('auth.password.strength.strong'),
  ];
  const colors = [
    'bg-destructive',
    'bg-destructive',
    'bg-amber-500',
    'bg-amber-400',
    'bg-green-500',
  ];
  return (
    <div id={id} className="space-y-1" aria-live="polite">
      <div className="flex gap-1">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className={cn(
              'h-1 flex-1 rounded-full transition-colors',
              i < score ? colors[score] : 'bg-muted',
            )}
          />
        ))}
      </div>
      <p className="text-muted-foreground text-xs">{labels[score]}</p>
    </div>
  );
}

function GoogleIcon({ className }: { className?: string }): React.JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        fill="#EA4335"
        d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6.2s2.7-6.2 6-6.2c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.7 3.3 14.6 2.4 12 2.4 6.7 2.4 2.4 6.7 2.4 12s4.3 9.6 9.6 9.6c5.5 0 9.2-3.9 9.2-9.4 0-.6-.1-1.1-.2-1.6H12z"
      />
    </svg>
  );
}
